import { useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import { usePocketBase } from "../hooks/usePocketBase";

type SignUpFormState = {
  name: string;
  email: string;
  password: string;
  passwordConfirm: string;
};

const initialState: SignUpFormState = {
  name: "",
  email: "",
  password: "",
  passwordConfirm: "",
};

export function SignUpPage() {
  const pb = usePocketBase();
  const [form, setForm] = useState<SignUpFormState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  function handleChange<K extends keyof SignUpFormState>(
    field: K,
    value: SignUpFormState[K],
  ) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (form.password !== form.passwordConfirm) {
      setErrorMessage("Passwords do not match.");
      setSuccessMessage(null);
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      await pb.collection("users").create({
        email: form.email,
        password: form.password,
        passwordConfirm: form.passwordConfirm,
        name: form.name,
      });

      setSuccessMessage("Account created successfully. You can now sign in.");
      setForm(initialState);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create account.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card variant="outlined">
      <CardContent>
          <Typography component="h1" variant="h4" sx={{ mb: 1 }}>
            Create account
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            Sign up to get started.
          </Typography>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Name"
              margin="normal"
              value={form.name}
              onChange={(event) => handleChange("name", event.target.value)}
            />

            <TextField
              fullWidth
              required
              autoComplete="email"
              label="Email"
              margin="normal"
              type="email"
              value={form.email}
              onChange={(event) => handleChange("email", event.target.value)}
            />

            <TextField
              fullWidth
              required
              autoComplete="new-password"
              label="Password"
              margin="normal"
              type="password"
              value={form.password}
              onChange={(event) => handleChange("password", event.target.value)}
            />

            <TextField
              fullWidth
              required
              autoComplete="new-password"
              label="Confirm Password"
              margin="normal"
              type="password"
              value={form.passwordConfirm}
              onChange={(event) =>
                handleChange("passwordConfirm", event.target.value)
              }
            />

            <Button
              fullWidth
              disabled={isSubmitting}
              sx={{ mt: 3 }}
              type="submit"
              variant="contained"
            >
              {isSubmitting ? "Creating account..." : "Sign up"}
            </Button>
          </Box>
      </CardContent>
    </Card>
  );
}
